import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ErrorDeApi, type PaginaDeCfdi, type TipoComprobante } from '../servicios/api'

const TIPOS: { valor: TipoComprobante | ''; etiqueta: string }[] = [
  { valor: '', etiqueta: 'Todos' },
  { valor: 'Ingreso', etiqueta: 'Ingreso' },
  { valor: 'Egreso', etiqueta: 'Egreso' },
  { valor: 'Pago', etiqueta: 'Pago' },
  { valor: 'Nomina', etiqueta: 'Nómina' },
  { valor: 'Traslado', etiqueta: 'Traslado' },
]

const ESPERA_DE_TECLEO = 300
const POR_PAGINA = 50

const dinero = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fecha = (iso: string) => new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })
const uuidCorto = (uuid: string) => (uuid.length > 17 ? `${uuid.slice(0, 8)}…${uuid.slice(-4)}` : uuid)

/**
 * Repositorio de CFDI de toda la cartera (F6, RF-504). La vista por cliente responde «¿qué le
 * llegó a este?»; con ocho clientes, saber dónde se están acumulando los comprobantes obligaba a
 * entrar uno por uno.
 *
 * Es de solo lectura a propósito. Importar, descargar el lote y verificar estatus son acciones
 * de un cliente concreto: ofrecerlas aquí obligaría a preguntar «¿de cuál?» en cada una, y esa
 * pregunta ya la contesta el enlace de cada renglón a su repositorio.
 */
export function RepositorioDelDespacho() {
  const [tipo, setTipo] = useState<TipoComprobante | ''>('')
  const [busqueda, setBusqueda] = useState('')
  const [busquedaAplicada, setBusquedaAplicada] = useState('')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [pagina, setPagina] = useState(1)
  const [datos, setDatos] = useState<PaginaDeCfdi>()
  const [error, setError] = useState('')

  // Sin la espera, cada tecla del buscador sería una consulta contra toda la cartera.
  useEffect(() => {
    const reloj = setTimeout(() => {
      setBusquedaAplicada(busqueda)
      setPagina(1)
    }, ESPERA_DE_TECLEO)
    return () => clearTimeout(reloj)
  }, [busqueda])

  useEffect(() => {
    let vigente = true
    api.cfdiDelDespacho({ tipo: tipo || undefined, busqueda: busquedaAplicada || undefined, pagina, desde, hasta })
      .then((r) => { if (vigente) { setDatos(r); setError('') } })
      .catch((e) => {
        if (vigente) setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cargar el repositorio del despacho.')
      })
    return () => { vigente = false }
  }, [tipo, busquedaAplicada, pagina, desde, hasta])

  if (error && !datos) return <p className="error" role="alert">{error}</p>
  if (!datos) return <p>Cargando…</p>

  const ultimaPagina = Math.max(1, Math.ceil(datos.total / POR_PAGINA))
  const clientes = new Set(datos.items.map((c) => c.clienteId))
  const pendientes = datos.items.filter((c) => c.estadoContabilizacion === 'Pendiente')

  return (
    <>
      <header className="encabezado">
        <h1>CFDI de la cartera</h1>
      </header>
      <p className="sutil">
        Todos los clientes que atiendes, en una sola tabla. Para importar, descargar o verificar
        estatus, entra al repositorio del cliente.
      </p>

      <div className="filtros">
        <input className="buscador" type="search" placeholder="UUID, RFC, nombre, folio o importe"
          style={{ width: 280, margin: 0 }} aria-label="Buscar en toda la cartera"
          value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        <div className="seg desplazable" role="radiogroup" aria-label="Tipo de comprobante">
          {TIPOS.map((t) => (
            <button key={t.valor || 'todos'} type="button" className="seg-opcion"
              role="radio" aria-checked={tipo === t.valor}
              onClick={() => { setTipo(t.valor); setPagina(1) }}>
              {t.etiqueta}
              {t.valor && datos.conteosPorTipo[t.valor] !== undefined && (
                <span className="sutil"> · {datos.conteosPorTipo[t.valor]}</span>
              )}
            </button>
          ))}
        </div>
        <label>
          Del
          <input type="date" className="compacta" value={desde}
            onChange={(e) => { setDesde(e.target.value); setPagina(1) }} />
        </label>
        <label>
          Al
          <input type="date" className="compacta" value={hasta}
            onChange={(e) => { setHasta(e.target.value); setPagina(1) }} />
        </label>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {datos.items.length === 0 ? (
        <p className="vacio">
          {datos.total === 0 && !busquedaAplicada && !tipo && !desde && !hasta
            ? 'Todavía no hay CFDI en tu cartera. Se importan desde el repositorio de cada cliente.'
            : 'Ningún comprobante coincide con el filtro.'}
        </p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th style={{ minWidth: 150 }}>Cliente</th>
                <th>Emisor / receptor</th>
                <th>UUID</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ whiteSpace: 'nowrap' }}>Estatus SAT</th>
                <th>Conciliación</th>
              </tr>
            </thead>
            <tbody>
              {datos.items.map((c) => (
                <tr key={c.id} className={c.estatus === 'Cancelado' ? 'resaltada' : undefined}>
                  <td>
                    <Link to={`/clientes/${c.clienteId}/cfdi`}>{c.clienteRazonSocial}</Link>
                  </td>
                  <td>
                    {c.esEmitido ? c.nombreReceptor : c.nombreEmisor}{' '}
                    <span className="sutil mono">{c.esEmitido ? c.rfcReceptor : c.rfcEmisor}</span>
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }} title={c.uuid}>{uuidCorto(c.uuid)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.tipo} <span className="sutil">{c.esEmitido ? 'emitido' : 'recibido'}</span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fecha(c.fechaEmision)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{dinero(c.total)}</td>
                  <td>
                    <span className={`insignia ${c.estatus === 'Cancelado' ? 'vencido' : 'vigente'}`}>
                      {c.estatus}
                    </span>
                  </td>
                  <td>
                    {c.estadoContabilizacion === 'Contabilizado' ? (
                      <Link className="insignia neutra" to={`/clientes/${c.clienteId}/polizas`}>
                        En póliza {c.polizaFolioVisible}
                      </Link>
                    ) : c.estadoContabilizacion === 'Descartado' ? (
                      <span className="insignia descartada">Descartado</span>
                    ) : (
                      <Link className="insignia suave" to={`/clientes/${c.clienteId}/contabilizacion`}>
                        Pendiente
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="filtros" style={{ justifyContent: 'space-between' }}>
        <span className="nota" style={{ margin: 0 }}>
          Mostrando {datos.items.length} de {datos.total} · {clientes.size}{' '}
          {clientes.size === 1 ? 'cliente' : 'clientes'} en esta página · {pendientes.length} sin
          contabilizar · deduplicación por UUID (RN-04).
        </span>
        {ultimaPagina > 1 && (
          <span>
            <button className="compacta" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
              ← Anterior
            </button>{' '}
            <span className="sutil">Página {pagina} de {ultimaPagina}</span>{' '}
            <button className="compacta" disabled={pagina >= ultimaPagina} onClick={() => setPagina((p) => p + 1)}>
              Siguiente →
            </button>
          </span>
        )}
      </div>
    </>
  )
}
